// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ProjectPage.test.tsx
import {
  askPunttOpenItemKindSchema,
  type AskPunttOpenItem,
  type AskPunttOpenItemKind,
} from "@mg/schemas/src/christo/askPunttOpenItems";
import { CaretUp } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { openItemKindLabel } from "./openItemKindLabel";

type SectionKey = "waiting-on-you" | "waiting-on-others";
type FilterId = "all" | AskPunttOpenItemKind;

const CHIP_KINDS = [
  "source_conflict",
  "expert_question",
  "missing_input",
] as const satisfies readonly AskPunttOpenItemKind[];

const GROUP_ORDER: AskPunttOpenItemKind[] = [
  ...CHIP_KINDS,
  "project_blocker",
  "vague_requirement",
];

/**
 * why: a visible open item is status `open` (cleared rows are filtered out
 * before this list). "Waiting on you" is the subset owned by the viewer.
 * Everything else is "Waiting on others".
 */
function statusSections(
  items: readonly AskPunttOpenItem[],
  currentUserId: string | undefined,
): { key: SectionKey; items: AskPunttOpenItem[] }[] {
  const waitingOnYou = items.filter(
    (item) =>
      currentUserId != null &&
      currentUserId.length > 0 &&
      item.ownerUserId === currentUserId,
  );
  const waitingOnYouIds = new Set(waitingOnYou.map((item) => item.id));
  const others = items.filter((item) => !waitingOnYouIds.has(item.id));
  const sections: { key: SectionKey; items: AskPunttOpenItem[] }[] = [];
  if (waitingOnYou.length > 0) {
    sections.push({ key: "waiting-on-you", items: waitingOnYou });
  }
  if (others.length > 0) {
    sections.push({ key: "waiting-on-others", items: others });
  }
  return sections;
}

function groupOrder(items: readonly AskPunttOpenItem[]): string[] {
  const known = new Set<string>(GROUP_ORDER);
  const extra = Array.from(
    new Set(items.map((item) => item.kind).filter((kind) => !known.has(kind))),
  );
  return [...GROUP_ORDER, ...extra];
}

function orderedGroups(
  items: readonly AskPunttOpenItem[],
): { kind: string; items: AskPunttOpenItem[] }[] {
  return groupOrder(items).flatMap((kind) => {
    const rows = items.filter((item) => item.kind === kind);
    if (rows.length === 0) return [];
    return [{ kind, items: rows }];
  });
}

// why: comment findings stay on the review. Leaving them in this list puts a Findings group, chip, and severity line back in the pane.
export function paneItems(
  items: readonly AskPunttOpenItem[],
): AskPunttOpenItem[] {
  return items.filter((item) => item.kind !== "unjudged_finding");
}

function CollapseHeading({
  as: Heading,
  testId,
  labelId,
  label,
  count,
  open,
  onToggle,
  className,
  countClassName,
}: {
  as: "h3" | "h4";
  testId: string;
  labelId?: string;
  label: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  className: string;
  countClassName: string;
}) {
  return (
    <Heading
      id={labelId}
      data-testid={`${testId}-heading`}
      aria-label={`${label} ${count}`}
      className={className}
    >
      <button
        type="button"
        data-testid={`${testId}-toggle`}
        aria-expanded={open}
        // why: a closed block's list is not rendered, and aria-controls must name an element that exists.
        aria-controls={open ? `${testId}-list` : undefined}
        onClick={onToggle}
        className="flex w-full items-baseline gap-2 bg-transparent text-left"
      >
        <CaretUp
          size={12}
          weight="bold"
          aria-hidden="true"
          className={`shrink-0 ${open ? "" : "rotate-180"}`}
        />
        <span>{label}</span>
        <span className={countClassName} data-testid={`${testId}-count`}>
          {count}
        </span>
      </button>
    </Heading>
  );
}

export function AskV2OpenItemsGroups({
  items,
  currentUserId,
  renderRows,
}: {
  items: AskPunttOpenItem[];
  currentUserId: string | undefined;
  renderRows: (rows: AskPunttOpenItem[]) => ReactNode;
}) {
  const { t } = useTranslation("ui");
  const [filter, setFilter] = useState<FilterId>("all");
  // why: collapse is per header. Closing "Waiting on you" must not close
  // "Waiting on others", and a kind stays open when its section reopens.
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const toggleCollapsed = (key: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const sectionLabels = {
    "waiting-on-you": t("askV2.openItems.waitingOnYou"),
    "waiting-on-others": t("askV2.openItems.waitingOnOthers"),
  } as const;
  const listed = paneItems(items);
  const chipLabels: Record<(typeof CHIP_KINDS)[number], string> = {
    source_conflict: t("askV2.openItems.filterSourceConflicts"),
    expert_question: t("askV2.openItems.filterExpertQuestions"),
    missing_input: t("askV2.openItems.filterMissingInputs"),
  };
  const chips: { id: FilterId; label: string; count: number }[] = [
    { id: "all", label: t("askV2.openItems.filterAll"), count: listed.length },
    ...CHIP_KINDS.map((kind) => ({
      id: kind,
      label: chipLabels[kind],
      count: listed.filter((item) => item.kind === kind).length,
    })),
  ];
  const visible =
    filter === "all" ? listed : listed.filter((item) => item.kind === filter);

  return (
    <div className="flex flex-col gap-6" data-testid="ask-v2-open-items-list">
      <div
        className="flex flex-wrap gap-1.5"
        data-testid="ask-v2-open-items-filters"
      >
        {chips.map((chip) => {
          const active = filter === chip.id;
          return (
            <button
              key={chip.id}
              type="button"
              data-testid={`ask-v2-open-items-filter-${chip.id}`}
              aria-pressed={active}
              onClick={() => setFilter(chip.id)}
              className={`focus-visible:outline-puntt-brand-purple-9 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] leading-snug focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                active
                  ? "bg-puntt-neutral-gray-4 font-medium text-puntt-neutral-gray-12"
                  : "bg-puntt-neutral-gray-2 font-normal text-puntt-neutral-gray-11"
              }`}
            >
              <span>{chip.label}</span>
              <span data-testid={`ask-v2-open-items-filter-${chip.id}-count`}>
                {chip.count}
              </span>
            </button>
          );
        })}
      </div>
      {visible.length === 0 ? (
        <Text size="2" data-testid="ask-v2-open-items-empty">
          {t("askV2.openItems.empty")}
        </Text>
      ) : null}
      {statusSections(visible, currentUserId).map((section) => {
        const sectionOpen = !collapsed.has(section.key);
        const sectionTestId = `ask-v2-review-section-${section.key}`;
        return (
          <section
            key={section.key}
            data-testid={sectionTestId}
            aria-labelledby={`ask-v2-open-items-section-${section.key}-label`}
            className="flex flex-col gap-3"
          >
            <CollapseHeading
              as="h3"
              testId={sectionTestId}
              labelId={`ask-v2-open-items-section-${section.key}-label`}
              label={sectionLabels[section.key]}
              count={section.items.length}
              open={sectionOpen}
              onToggle={() => toggleCollapsed(section.key)}
              className="px-3 text-[15px] font-semibold text-puntt-neutral-gray-12"
              countClassName="text-[13px] font-normal text-puntt-neutral-gray-10"
            />
            {sectionOpen ? (
              <div
                id={`${sectionTestId}-list`}
                data-testid={`${sectionTestId}-list`}
              >
                {orderedGroups(section.items).map((group) => {
                  const known = askPunttOpenItemKindSchema.safeParse(
                    group.kind,
                  );
                  const label =
                    group.kind in chipLabels
                      ? chipLabels[group.kind as (typeof CHIP_KINDS)[number]]
                      : known.success
                        ? openItemKindLabel(known.data, t)
                        : group.kind;
                  const key = `${section.key}-${group.kind}`;
                  const groupOpen = !collapsed.has(key);
                  return (
                    <div
                      key={key}
                      data-testid={`ask-v2-review-section-${key}`}
                      className="flex flex-col gap-1 border-b border-puntt-neutral-gray-5 pb-2"
                    >
                      <CollapseHeading
                        as="h4"
                        testId={`ask-v2-review-section-${key}`}
                        label={label}
                        count={group.items.length}
                        open={groupOpen}
                        onToggle={() => toggleCollapsed(key)}
                        className="px-3 text-[13px] font-medium text-puntt-neutral-gray-11"
                        countClassName="font-normal text-puntt-neutral-gray-10"
                      />
                      {groupOpen ? (
                        <ul
                          id={`ask-v2-review-section-${key}-list`}
                          data-testid={`ask-v2-review-section-${key}-list`}
                        >
                          {renderRows(group.items)}
                        </ul>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}
